import './index.css';
import { useState, useRef, useMemo, useCallback, Children, useEffect, isValidElement } from 'react';
import { keyboardMatch, keyboardKeys, keyboardMatches } from '@hh.ru/magritte-common-keyboard';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { s as styles, T as Tab } from './Tab-DqAw76GN.js';
import 'react/jsx-runtime';
import 'classnames';
import '@hh.ru/magritte-common-focus-visible';
import '@hh.ru/magritte-common-use-multiple-refs';
import '@hh.ru/magritte-ui-badge';
import '@hh.ru/magritte-ui-scrollable-container';
import '@hh.ru/magritte-ui-tooltip';
import '@hh.ru/magritte-ui-typography';

const isValidTabElement = (child) => isValidElement(child) && child.type === Tab;
const isItemVisible = (child) => !child.props.hidden;
const useTabs = ({ activeItemId, indicatorRef, items, onChange, tabsContainerRef, }) => {
    const [selectedItemId, setSelectedItemId] = useState(activeItemId || null);
    const [focusedItemId, setFocusedItemId] = useState(null);
    const [focused, setFocused] = useState(false);
    const animateIndicatorRef = useRef(false);
    const isControlled = useRef(!!activeItemId && !!onChange);
    const itemRefs = useRef({});
    const { isXS } = useBreakpoint();
    const itemRefCallbacks = useMemo(() => {
        return items.reduce((result, item) => {
            return {
                ...result,
                [item.props.id]: (element) => {
                    itemRefs.current[item.props.id] = element;
                },
            };
        }, {});
    }, [items]);
    const handleSelect = useCallback((itemId) => {
        if (!isXS) {
            setFocusedItemId(itemId);
        }
        if (!isControlled.current) {
            setSelectedItemId(itemId);
        }
        if (onChange) {
            onChange(itemId);
        }
    }, [isXS, onChange, setFocusedItemId, setSelectedItemId]);
    const tabs = useMemo(() => {
        return Children.toArray(items)
            .filter((item) => isValidTabElement(item) && isItemVisible(item))
            .map((item, index) => {
            const itemId = item.props.id;
            const isSelected = isControlled?.current ? itemId === activeItemId : itemId === selectedItemId;
            const tabIndex = index === 0 ? 0 : -1;
            return {
                ...item.props,
                tabIndex: focused ? -1 : tabIndex,
                selected: isSelected,
                onSelect: handleSelect,
                focused: itemId === focusedItemId,
                itemRef: itemRefCallbacks[itemId],
                ref: item.ref,
            };
        });
    }, [activeItemId, focused, focusedItemId, handleSelect, items, itemRefCallbacks, selectedItemId]);
    const getNextTab = useCallback((currentTab) => {
        const itemIndex = tabs.findIndex(({ id }) => id === currentTab);
        if (itemIndex === tabs.length - 1) {
            return currentTab;
        }
        return tabs[itemIndex + 1].id;
    }, [tabs]);
    const getPrevTab = useCallback((currentTab) => {
        const itemIndex = tabs.findIndex(({ id }) => id === currentTab);
        if (itemIndex <= 0) {
            return currentTab;
        }
        return tabs[itemIndex - 1].id;
    }, [tabs]);
    const getFirstTab = useCallback(() => tabs[0].id, [tabs]);
    const getLastTab = useCallback(() => tabs[tabs.length - 1].id, [tabs]);
    const handleKeydown = useCallback((event) => {
        if (keyboardMatch(event, keyboardKeys.Tab)) {
            return;
        }
        const defaultFocusedItem = getFirstTab();
        if (keyboardMatch(event, keyboardKeys.ArrowLeft)) {
            setFocusedItemId(getPrevTab(focusedItemId || defaultFocusedItem));
        }
        else if (keyboardMatch(event, keyboardKeys.ArrowRight)) {
            setFocusedItemId(getNextTab(focusedItemId || defaultFocusedItem));
        }
        else if (keyboardMatch(event, keyboardKeys.Home)) {
            setFocusedItemId(getFirstTab());
        }
        else if (keyboardMatch(event, keyboardKeys.End)) {
            setFocusedItemId(getLastTab());
        }
        else if (keyboardMatches(event, [keyboardKeys.Enter, keyboardKeys.Space])) {
            handleSelect(focusedItemId || defaultFocusedItem);
            return;
        }
        else {
            return;
        }
        event.preventDefault();
    }, [getFirstTab, getNextTab, getPrevTab, focusedItemId, getLastTab, handleSelect]);
    useEffect(() => {
        const itemId = isControlled ? activeItemId : selectedItemId;
        if (!!itemId &&
            indicatorRef.current !== null &&
            tabsContainerRef.current !== null &&
            itemRefs.current[itemId]) {
            if (!animateIndicatorRef.current) {
                animateIndicatorRef.current = true;
            }
            else {
                indicatorRef.current.classList.add(styles.animate);
            }
            indicatorRef.current.style.left = `${itemRefs.current[itemId].offsetLeft}px`;
            indicatorRef.current.style.width = `${itemRefs.current[itemId].offsetWidth}px`;
        }
    }, [activeItemId, selectedItemId, indicatorRef, tabsContainerRef]);
    useEffect(() => {
        const handleFocusIn = (event) => {
            if (focused && tabsContainerRef?.current?.contains(event.relatedTarget)) {
                return;
            }
            setFocused(true);
        };
        const handleFocusOut = (event) => {
            if (tabsContainerRef?.current?.contains(event.relatedTarget)) {
                return;
            }
            setFocused(false);
            setFocusedItemId(null);
        };
        const element = tabsContainerRef?.current;
        if (focused) {
            element?.addEventListener('focusout', handleFocusOut);
            return () => {
                element?.removeEventListener('focusout', handleFocusOut);
            };
        }
        element?.addEventListener('focusin', handleFocusIn);
        return () => {
            element?.removeEventListener('focusin', handleFocusIn);
        };
    }, [focused, tabsContainerRef]);
    useEffect(() => {
        const element = tabsContainerRef?.current;
        element?.addEventListener('keydown', handleKeydown);
        return () => {
            element?.removeEventListener('keydown', handleKeydown);
        };
    }, [handleKeydown, tabsContainerRef]);
    return tabs;
};

export { useTabs };
//# sourceMappingURL=useTabs.js.map
